import { ConversationSearchResult } from '@api';
import { Button } from '@design/components/button/Button';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { useDebounce } from '@helpers';
import { SearchModalBase } from '@terminal/components/search-modal-base/SearchModalBase';
import { QUERY_DELAY } from '@terminal/config/constants';
import { appPaths } from '@terminal/routes/app-paths';
import { UIEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ChatCard } from '../chat-card/ChatCard';
import { useFetchInfiniteSearchConversation } from '../hooks/useFetchInfiniteConversation';
import { useChat } from '../use-chat/useChat';
import styles from './SearchChat.module.scss';

export const isConversationSearchResult = (
  item: unknown
): item is ConversationSearchResult => {
  return item !== null && typeof item === 'object';
};

export const SearchChat = ({
  isHistoryExpanded,
  setIsOpen,
  isOpen,
}: {
  isHistoryExpanded?: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['chat', 'widget']);

  const [searchString, setSearchString] = useState<string>();
  const debouncedQueryString = useDebounce(searchString ?? '', QUERY_DELAY);

  const getActiveConversationTasks = useChat(
    state => state.getActiveConversationTasks
  );

  const handleModalClose = () => {
    setSearchString(undefined);
    setIsOpen(false);
  };

  const { data, isLoading, hasNextPage, fetchNextPage } =
    useFetchInfiniteSearchConversation({
      queryString: debouncedQueryString,
      shouldFetch: isOpen,
    });

  const handleResultClick = (itemId: string) => {
    navigate({
      pathname: appPaths.alfred,
      search: new URLSearchParams({ conversationId: itemId }).toString(),
    });

    getActiveConversationTasks(itemId);
    handleModalClose();
  };

  const renderLoadingNode = () => {
    return (
      <div>
        <div className={styles.skeletonWrapper}>
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '200px',
                  height: '24px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '360px',
                  height: '24px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
        </div>
        <div className={styles.skeletonWrapper}>
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '200px',
                  height: '24px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '360px',
                  height: '24px',
                  borderRadius: '8px',
                },
              },
            ]}
          />
        </div>
      </div>
    );
  };

  const searchResult = data?.pages.flatMap(page => page) || [];

  const handleRefetch = (e: UIEvent<HTMLElement>) => {
    const element = e.target;

    if (element instanceof HTMLElement) {
      const isScrollToBottom =
        element.scrollTop + element.clientHeight >= element.scrollHeight;

      if (isScrollToBottom && !isLoading && hasNextPage) {
        fetchNextPage();
      }
    }
  };

  return (
    <>
      <Tooltip
        label={t('chat:historySidebar.searchConversation')}
        disabled={isHistoryExpanded}
        trigger={TooltipTrigger.Hover}
        placement="right"
      >
        <Button
          className={styles.styledButton}
          variant={'empty'}
          size="small"
          iconName="Search"
          onClick={() => setIsOpen(!isOpen)}
        />
      </Tooltip>
      {isOpen && (
        <div
          className={styles.styledSearchModalBase}
          onScrollCapture={(e: UIEvent<HTMLElement>) => {
            handleRefetch(e);
          }}
          onClick={e => e.stopPropagation()}
        >
          <SearchModalBase
            title={t('chat:searchChats')}
            noSearchResultsLabel={t('widget:search.noSearchResults')}
            renderDropdownItem={item => {
              const isSearchResult = isConversationSearchResult(item);

              return (
                <>
                  {isLoading
                    ? renderLoadingNode()
                    : isSearchResult && (
                        <ChatCard
                          title={item.name}
                          summary={item.state.content}
                          updatedAt={item.updated_at}
                          searchString={searchString}
                        />
                      )}
                </>
              );
            }}
            beforeResultsNode={
              <>
                {!isLoading && !!searchResult.length && (
                  <h4 className={styles.searchDescription}>
                    {t('chat:chatsSorted')}
                  </h4>
                )}
              </>
            }
            searchValue={searchString || ''}
            handleSearch={e => {
              setSearchString(e);
            }}
            selectItem={item => {
              const isSearchResult = isConversationSearchResult(item);

              if (isSearchResult) {
                handleResultClick(item.conversation_id);
              }
            }}
            dropdownItems={searchResult}
            isLoading={false}
            isOpen={isOpen}
            onClose={handleModalClose}
            loadingNode={<></>}
            shouldResetScrollOnItemsChange={false}
          />
        </div>
      )}
    </>
  );
};
